import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Check, Search, X as XIcon, ChevronRight, ChevronDown, ChevronLeft, Plus, Trash2, Bell, Clock } from 'lucide-react';
import DeleteButton from './ui/DeleteButton';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../utils/api';

const fmt12 = (time) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

// Fixed-position dropdown — mirrors PeopleTab RoleSelect pattern:
//   default → role members only  |  "Search all members…" → full search
const MemberDropdown = ({
  buttonRect, role, currentMemberId,
  roleMemberIds, allMembersList,
  blockedMemberIds,
  onSelect,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  // Focus search input when switching to "show all" mode
  useEffect(() => {
    if (showAll) {
      const t = setTimeout(() => searchRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [showAll]);

  // Members who carry this specific role (shown by default)
  const roleMembers = useMemo(
    () => allMembersList.filter(m => roleMemberIds.has(m.member_id)),
    [allMembersList, roleMemberIds],
  );

  // All members filtered by search query
  const filteredAll = useMemo(() => {
    const q = search.toLowerCase();
    return q ? allMembersList.filter(m => m.name.toLowerCase().includes(q)) : allMembersList;
  }, [allMembersList, search]);

  // Flip above if near bottom of viewport
  const left = Math.max(4, Math.min(buttonRect.left, window.innerWidth - 232));
  const top = window.innerHeight - buttonRect.bottom > 280
    ? buttonRect.bottom + 4
    : buttonRect.top - 284;

  const renderMemberRow = (m) => {
    const blocked = blockedMemberIds?.has(m.member_id);
    const isCurrent = currentMemberId === m.member_id;
    return (
      <button
        key={m.member_id}
        onClick={() => { if (!blocked) onSelect(m.member_id); }}
        disabled={blocked}
        title={blocked ? 'Unavailable on this date' : undefined}
        className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between transition-colors ${
          blocked
            ? 'opacity-40 cursor-not-allowed text-slate-400 dark:text-slate-500'
            : isCurrent
              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium'
              : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
        }`}
      >
        <span className={`truncate ${blocked ? 'line-through' : ''}`}>{m.name}</span>
        {isCurrent && !blocked && <Check className="w-3 h-3 flex-shrink-0 ml-2 text-green-600 dark:text-green-400" />}
      </button>
    );
  };

  return (
    <div
      data-scheduler-dropdown="true"
      style={{ position: 'fixed', top, left, zIndex: 9999, width: 228 }}
      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden"
    >
      {/* Search bar — visible only in "show all" mode */}
      {showAll && (
        <div className="flex items-center border-b border-slate-200 dark:border-slate-700 px-3">
          <Search className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 mr-2 flex-shrink-0" />
          <input
            ref={searchRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.stopPropagation()}
            placeholder="Search all members…"
            className="flex-1 h-9 text-xs outline-none bg-transparent placeholder:text-slate-400 dark:placeholder:text-slate-500 text-slate-900 dark:text-slate-100"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 ml-1">
              <XIcon className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      <div className="max-h-60 overflow-y-auto">
        {/* None / clear */}
        <button
          onClick={() => onSelect('')}
          className="w-full text-left px-3 py-2 text-xs text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700/60"
        >
          — None —
        </button>

        {!showAll ? (
          <>
            {/* Role members */}
            {roleMembers.length > 0 ? (
              <>
                <div className="px-3 pt-2 pb-0.5 text-[10px] font-semibold uppercase text-slate-400 dark:text-slate-500 tracking-wider">
                  {role.name}
                </div>
                {roleMembers.map(m => renderMemberRow(m))}
              </>
            ) : (
              <div className="px-3 py-2.5 text-xs text-slate-400 dark:text-slate-500">
                No members with this role
              </div>
            )}

            {/* Search all members button */}
            <div className="border-t border-slate-100 dark:border-slate-700/60 mt-1">
              <button
                onClick={() => setShowAll(true)}
                className="w-full text-left px-3 py-2 text-xs text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2"
              >
                <Search className="w-3 h-3 flex-shrink-0" />
                Search all members…
              </button>
            </div>
          </>
        ) : (
          /* Show-all mode */
          <>
            <div className="px-3 pt-2 pb-0.5 text-[10px] font-semibold uppercase text-slate-400 dark:text-slate-500 tracking-wider">
              {search ? `Results for "${search}"` : 'All members'}
            </div>
            {filteredAll.length > 0 ? filteredAll.map(m => renderMemberRow(m)) : (
              <div className="px-3 py-4 text-xs text-slate-400 dark:text-slate-500 text-center">
                {search ? 'No members found' : 'No members available'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const SchedulerGrid = ({ services, roles, groups, allMembers, allBlocks, onAssign, onAddTeam, onRemoveTeam, onFlushSave, savingServices, readOnly }) => {
  const navigate = useNavigate();
  const [activeCell, setActiveCell] = useState(null);
  const [notifyingServices, setNotifyingServices] = useState(new Set());
  const [serviceColCollapsed, setServiceColCollapsed] = useState(false);

  // Compute blocked member IDs for a given service date
  const getBlockedForDate = useCallback((serviceDate) => {
    if (!allBlocks?.length || !serviceDate) return new Set();
    return new Set(
      (allBlocks || [])
        .filter(b => b.start_date <= serviceDate && serviceDate <= (b.end_date || b.start_date))
        .map(b => b.member_id)
    );
  }, [allBlocks]);

  // Service column widths
  const SVC_W = serviceColCollapsed ? 80 : 150;

  const handleNotify = useCallback(async (e, serviceId) => {
    e.stopPropagation();
    if (notifyingServices.has(serviceId)) return;
    setNotifyingServices(prev => new Set([...prev, serviceId]));
    try {
      const res = await api.post(`/services/${serviceId}/notify`);
      const { message, sent } = res.data;
      if (sent > 0) toast.success(message);
      else toast.info(message);
    } catch {
      toast.error('Failed to send notifications');
    } finally {
      setNotifyingServices(prev => { const n = new Set(prev); n.delete(serviceId); return n; });
    }
  }, [notifyingServices]);

  const columns = useMemo(() => {
    const groupOrder = groups.map(g => g.name);
    const grouped = {};
    for (const r of roles) {
      if (!grouped[r.group]) grouped[r.group] = [];
      grouped[r.group].push(r);
    }
    for (const g of Object.keys(grouped)) {
      grouped[g].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return groupOrder.filter(g => grouped[g]).map(g => ({ groupName: g, roles: grouped[g] }));
  }, [roles, groups]);

  const flatRoles = useMemo(() => columns.flatMap(c => c.roles), [columns]);

  // ── Collapse / expand groups ──────────────────────────────────────────────
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const toggleGroup = useCallback((groupName) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
    setActiveCell(null);
  }, []);

  // Table width: service col + 40px per collapsed group + 110px per expanded role
  const tableWidth = useMemo(() => {
    return SVC_W + columns.reduce((acc, col) => {
      return acc + (collapsedGroups.has(col.groupName) ? 40 : col.roles.length * 110);
    }, 0);
  }, [SVC_W, columns, collapsedGroups]);

  const memberById = useMemo(() => {
    const m = {};
    for (const mem of allMembers) m[mem.member_id] = mem;
    return m;
  }, [allMembers]);

  const { roleMemberSets } = useMemo(() => {
    const roleSets = {};
    for (const mem of allMembers) {
      for (const roleName of (mem.roles || [])) {
        const roleObj = roles.find(r => r.name === roleName);
        if (roleObj) {
          if (!roleSets[roleName]) roleSets[roleName] = new Set();
          roleSets[roleName].add(mem.member_id);
        }
      }
    }
    return { roleMemberSets: roleSets };
  }, [allMembers, roles]);

  // Close dropdown on click-outside
  useEffect(() => {
    if (!activeCell) return;
    const handler = (e) => {
      if (!e.target.closest('[data-scheduler-dropdown]') && !e.target.closest('[data-scheduler-cell]')) {
        setActiveCell(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeCell]);

  useEffect(() => {
    if (!activeCell) return;
    const handler = (e) => { if (e.key === 'Escape') { setActiveCell(null); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [activeCell]);

  const handleCellClick = useCallback((e, serviceId, groupName, roleName) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (activeCell?.serviceId === serviceId && activeCell?.roleName === roleName) {
      setActiveCell(null);
    } else {
      setActiveCell({ serviceId, groupName, roleName, rect });
    }
  }, [activeCell]);

  const handleAssign = useCallback((serviceId, groupName, roleName, memberId) => {
    onAssign(serviceId, groupName, roleName, memberId);
    setActiveCell(null);
  }, [onAssign]);

  // Alternating row colours — light + dark
  const rowBgLight = ['bg-white dark:bg-slate-900', 'bg-slate-50/60 dark:bg-slate-800/60'];

  // Border tokens — one place to change them for the whole grid
  const B_GROUP = 'border-b border-r-2 border-r-slate-300 dark:border-r-slate-600';  // last col of a group
  const B_STICKY_COL = 'border-r-2 border-r-slate-300 dark:border-r-slate-600';      // sticky service col right edge

  return (
    <div className="overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm h-full">
      <table
        data-testid="scheduler-grid-table"
        style={{ tableLayout: 'fixed', width: `${tableWidth}px`, borderCollapse: 'separate', borderSpacing: 0 }}
        className="text-xs"
      >
        <thead className="sticky top-0 z-20">
          {/* Row 1 — Team group headers */}
          <tr>
            <th
              className={`sticky left-0 z-30 bg-slate-100 dark:bg-slate-800 ${B_STICKY_COL} border-b border-b-slate-200 dark:border-b-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors`}
              style={{ width: SVC_W }}
              onClick={() => setServiceColCollapsed(v => !v)}
              title={serviceColCollapsed ? 'Expand service column' : 'Collapse service column'}
              data-testid="service-col-toggle"
            >
              <div className="flex items-center justify-end pr-1.5 h-full">
                {serviceColCollapsed
                  ? <ChevronRight className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                  : <ChevronLeft className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                }
              </div>
            </th>
            {columns.map(col => {
              const isCollapsed = collapsedGroups.has(col.groupName);
              return (
                <th
                  key={col.groupName}
                  colSpan={isCollapsed ? 1 : col.roles.length}
                  onClick={() => toggleGroup(col.groupName)}
                  className={`${B_GROUP} cursor-pointer select-none bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors ${
                    isCollapsed ? 'px-0 py-2' : 'py-1.5 px-2'
                  }`}
                >
                  {isCollapsed ? (
                    /* Vertical layout when collapsed — text rotated to fit the 40px column */
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <ChevronRight className="w-3 h-3 opacity-60 flex-shrink-0" />
                      <span
                        style={{ writingMode: 'vertical-lr', letterSpacing: '0.05em' }}
                        className="text-[10px] font-bold uppercase"
                      >
                        {col.groupName}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wider">
                      <span>{col.groupName}</span>
                      <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
                    </div>
                  )}
                </th>
              );
            })}
          </tr>

          {/* Row 2 — Individual role name headers */}
          <tr>
            <th
              className={`sticky left-0 z-30 bg-slate-100 dark:bg-slate-800 ${B_STICKY_COL} border-b-2 border-b-slate-300 dark:border-b-slate-600 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 ${serviceColCollapsed ? 'px-1 text-center' : 'px-3'}`}
            >
              {!serviceColCollapsed && 'Service'}
            </th>
            {columns.flatMap(col => {
              const isCollapsed = collapsedGroups.has(col.groupName);
              if (isCollapsed) {
                return [(
                  <th
                    key={`hdr-${col.groupName}`}
                    style={{ width: 40 }}
                    className="border-b-2 border-b-slate-300 dark:border-b-slate-600 border-r-2 border-r-slate-300 dark:border-r-slate-600 py-2 px-1 bg-slate-100 dark:bg-slate-800"
                  />
                )];
              }
              return col.roles.map((role, i) => {
                const isLast = i === col.roles.length - 1;
                return (
                  <th
                    key={role.role_id}
                    className={`py-2 px-1 text-center text-[11px] font-medium text-slate-600 dark:text-slate-300 leading-tight bg-slate-100 dark:bg-slate-800 border-b-2 border-b-slate-300 dark:border-b-slate-600 ${
                      isLast
                        ? 'border-r-2 border-r-slate-300 dark:border-r-slate-600'
                        : 'border-r border-r-slate-200 dark:border-r-slate-700'
                    }`}
                  >
                    {role.name}
                  </th>
                );
              });
            })}
          </tr>
        </thead>

        <tbody>
          {services.map((service, si) => {
            const addedGroups = new Set((service.team_assignments || []).map(t => t.group));
            const assignMap = {};
            for (const ta of (service.team_assignments || [])) {
              assignMap[ta.group] = {};
              for (const a of (ta.assignments || [])) {
                assignMap[ta.group][a.role] = a;
              }
            }
            const isSaving = savingServices.has(service.service_id);
            const rowBg = rowBgLight[si % 2];

            return (
              <tr
                key={service.service_id}
                data-testid={`grid-row-${service.service_id}`}
                className={`${rowBg} group/row hover:bg-green-50/30 dark:hover:bg-green-900/10 transition-colors`}
              >
                {/* Sticky service info cell */}
                <td
                  className={`sticky left-0 z-10 ${B_STICKY_COL} border-b border-b-slate-200 dark:border-b-slate-700 cursor-pointer ${rowBg} ${serviceColCollapsed ? 'px-1 py-2 text-center' : 'px-3 py-2'}`}
                  onClick={async () => {
                    if (onFlushSave) await onFlushSave(service.service_id);
                    navigate(`/services/${service.service_id}`);
                  }}
                  data-testid={`grid-service-cell-${service.service_id}`}
                >
                  {serviceColCollapsed ? (
                    /* Collapsed: date + time + status counters */
                    <div className="flex flex-col items-center gap-0.5">
                      <div className="font-bold text-slate-900 dark:text-slate-100 text-[10px] leading-tight flex items-center gap-0.5">
                        {format(new Date(service.date), 'dd MMM')}
                        {isSaving && <span className="w-2 h-2 border-[1.5px] border-green-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />}
                      </div>
                      <div className="text-slate-400 dark:text-slate-500 text-[9px]">{fmt12(service.time)}</div>
                      {/* Accepted / Declined / Pending counters */}
                      {(() => {
                        const all = (service.team_assignments || []).flatMap(ta => ta.assignments || []).filter(a => a.member_id);
                        const accepted = all.filter(a => a.status === 'accepted').length;
                        const declined = all.filter(a => a.status === 'declined').length;
                        const pending  = all.filter(a => !a.status || a.status === 'pending').length;
                        if (all.length === 0) return null;
                        return (
                          <div className="flex items-center gap-1 mt-0.5">
                            {accepted > 0 && (
                              <span className="flex items-center gap-0.5 text-emerald-500 dark:text-emerald-400 text-[9px]">
                                <Check className="w-2.5 h-2.5" />{accepted}
                              </span>
                            )}
                            {declined > 0 && (
                              <span className="flex items-center gap-0.5 text-rose-500 dark:text-rose-400 text-[9px]">
                                <XIcon className="w-2.5 h-2.5" />{declined}
                              </span>
                            )}
                            {pending > 0 && (
                              <span className="flex items-center gap-0.5 text-amber-500 dark:text-amber-400 text-[9px]">
                                <Clock className="w-2.5 h-2.5" />{pending}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    /* Expanded: full info + chevron + bell */
                    <div className="flex items-start gap-1">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 dark:text-slate-100 text-xs leading-tight flex items-center gap-1">
                          {format(new Date(service.date), 'dd MMM')}
                          {isSaving && (
                            <span className="w-2.5 h-2.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                          )}
                        </div>
                        <div className="text-slate-400 dark:text-slate-500 text-[10px]">{fmt12(service.time)}</div>
                        <div className="text-slate-500 dark:text-slate-400 text-[10px] leading-tight">{service.service_type}</div>
                        {service.name && service.name.trim().toLowerCase() !== (service.service_type || '').trim().toLowerCase() && (
                          <div className="text-slate-400 dark:text-slate-500 text-[10px] leading-tight italic">{service.name}</div>
                        )}
                        {/* Status counters — always visible in expanded mode */}
                        {(() => {
                          const all = (service.team_assignments || []).flatMap(ta => ta.assignments || []).filter(a => a.member_id);
                          if (all.length === 0) return null;
                          const accepted = all.filter(a => a.status === 'accepted').length;
                          const declined = all.filter(a => a.status === 'declined').length;
                          const pending  = all.filter(a => !a.status || a.status === 'pending').length;
                          return (
                            <div className="flex items-center gap-1 mt-0.5">
                              {accepted > 0 && <span className="flex items-center gap-0.5 text-emerald-500 dark:text-emerald-400 text-[9px]"><Check className="w-2.5 h-2.5" />{accepted}</span>}
                              {declined > 0 && <span className="flex items-center gap-0.5 text-rose-500 dark:text-rose-400 text-[9px]"><XIcon className="w-2.5 h-2.5" />{declined}</span>}
                              {pending > 0 && <span className="flex items-center gap-0.5 text-amber-500 dark:text-amber-400 text-[9px]"><Clock className="w-2.5 h-2.5" />{pending}</span>}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex flex-col items-center gap-1.5 flex-shrink-0 mt-0.5">
                        <ChevronRight className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 group-hover/row:text-green-500 dark:group-hover/row:text-green-400 transition-colors" />
                        {/* Notify bell — visible on row hover, hidden for read-only users */}
                        {!readOnly && (
                          <button
                            onClick={(e) => handleNotify(e, service.service_id)}
                            disabled={notifyingServices.has(service.service_id)}
                            className="opacity-0 group-hover/row:opacity-100 transition-opacity text-slate-400 dark:text-slate-500 hover:text-amber-500 dark:hover:text-amber-400 disabled:opacity-40"
                            title="Send assignment notifications to pending members"
                            data-testid={`notify-bell-${service.service_id}`}
                          >
                            {notifyingServices.has(service.service_id)
                              ? <span className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin block" />
                              : <Bell className="w-4 h-4" />
                            }
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </td>

                {/* Role cells */}
                {columns.flatMap(col => {
                  const isCollapsed = collapsedGroups.has(col.groupName);
                  const isGroupAdded = addedGroups.has(col.groupName);

                  if (isCollapsed) {
                    const groupAssignmentsMap = isGroupAdded ? (assignMap[col.groupName] || {}) : {};
                    const teamArr = Object.values(groupAssignmentsMap).filter(a => a.member_id);
                    const teamAccepted = teamArr.filter(a => a.status === 'accepted').length;
                    const teamDeclined = teamArr.filter(a => a.status === 'declined').length;
                    const teamPending  = teamArr.filter(a => !a.status || a.status === 'pending').length;
                    const hasAny = teamAccepted + teamDeclined + teamPending > 0;
                    return [(
                      <td
                        key={`col-${col.groupName}`}
                        style={{ width: 40 }}
                        className={`border-b border-b-slate-200 dark:border-b-slate-700 border-r-2 border-r-slate-300 dark:border-r-slate-600 text-center align-middle h-px ${!isGroupAdded ? 'bg-slate-50/80 dark:bg-slate-800/30' : ''}`}
                        title={isGroupAdded ? `${col.groupName}` : `${col.groupName} not added`}
                      >
                        {isGroupAdded ? (
                          <div className="relative flex items-center justify-center w-full h-full py-1">
                            {/* Per-team counters — fades out on row hover to reveal trash (write-only) */}
                            <div className={`flex flex-col items-center gap-0.5 ${!readOnly ? 'group-hover/row:opacity-0' : ''} transition-opacity`}>
                              {teamAccepted > 0 && <span className="flex items-center gap-0.5 text-emerald-500 dark:text-emerald-400 text-[9px]"><Check className="w-2 h-2" />{teamAccepted}</span>}
                              {teamDeclined > 0 && <span className="flex items-center gap-0.5 text-rose-500 dark:text-rose-400 text-[9px]"><XIcon className="w-2 h-2" />{teamDeclined}</span>}
                              {teamPending > 0 && <span className="flex items-center gap-0.5 text-amber-500 dark:text-amber-400 text-[9px]"><Clock className="w-2 h-2" />{teamPending}</span>}
                              {!hasAny && <span className="text-slate-300 dark:text-slate-600 text-[10px]">—</span>}
                            </div>
                            {!readOnly && (
                              <DeleteButton
                                onConfirm={() => onRemoveTeam?.(service.service_id, col.groupName)}
                                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity"
                                iconSize="w-3 h-3"
                                title={`Remove ${col.groupName} from this service`}
                                testId={`remove-team-collapsed-${service.service_id}-${col.groupName}`}
                              />
                            )}
                          </div>
                        ) : (
                          readOnly ? (
                            <span className="text-slate-300 dark:text-slate-600 text-[10px]">—</span>
                          ) : (
                            <button
                              onClick={() => onAddTeam?.(service.service_id, col.groupName)}
                              className="w-full h-full flex items-center justify-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                              title={`Add ${col.groupName} to this service`}
                              data-testid={`add-team-collapsed-${service.service_id}-${col.groupName}`}
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          )
                        )}
                      </td>
                    )];
                  }

                  // Team not yet added — one spanning cell with "Add team" link (write-only)
                  if (!isGroupAdded) {
                    return [(
                      <td
                        key={`not-added-${col.groupName}`}
                        colSpan={col.roles.length}
                        className="border-r-2 border-r-slate-300 dark:border-r-slate-600 border-b border-b-slate-200 dark:border-b-slate-700 bg-slate-50/80 dark:bg-slate-800/30 h-px align-middle text-center"
                        data-testid={`add-team-cell-${service.service_id}-${col.groupName}`}
                      >
                        {!readOnly && (
                          <button
                            onClick={() => onAddTeam?.(service.service_id, col.groupName)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:underline transition-colors"
                            data-testid={`add-team-btn-${service.service_id}-${col.groupName}`}
                          >
                            <Plus className="w-3 h-3 flex-shrink-0" />
                            Add team
                          </button>
                        )}
                      </td>
                    )];
                  }

                  return col.roles.map((role, i) => {
                    const isLast = i === col.roles.length - 1;
                    const borderClass = isLast
                      ? 'border-r-2 border-r-slate-300 dark:border-r-slate-600'
                      : 'border-r border-r-slate-200 dark:border-r-slate-700';

                    const assignment = assignMap[col.groupName]?.[role.name];
                    const memberId = assignment?.member_id || '';
                    const member = memberId ? memberById[memberId] : null;
                    const isActive = activeCell?.serviceId === service.service_id && activeCell?.roleName === role.name;

                    const statusClass = !member ? ''
                      : assignment?.status === 'accepted' ? 'text-emerald-600 dark:text-emerald-400 font-medium'
                      : assignment?.status === 'declined' ? 'text-rose-500 dark:text-rose-400 line-through opacity-70'
                      : 'text-amber-600 dark:text-amber-400';

                    const roleMemberSet = roleMemberSets[role.name] || new Set();

                    return (
                      <td
                        key={role.role_id}
                        className={`relative p-0 h-px ${borderClass} border-b border-b-slate-200 dark:border-b-slate-700`}
                        data-testid={`grid-cell-${service.service_id}-${role.role_id}`}
                      >
                        <button
                          data-scheduler-cell={readOnly ? undefined : 'true'}
                          onClick={readOnly ? undefined : e => handleCellClick(e, service.service_id, col.groupName, role.name)}
                          className={`w-full h-full min-h-[40px] px-1.5 py-2 flex items-center justify-center transition-colors ${
                            readOnly
                              ? 'cursor-default'
                              : isActive
                                ? 'bg-green-50 dark:bg-green-900/30 ring-2 ring-inset ring-green-400 dark:ring-green-500'
                                : member
                                  ? 'hover:bg-green-50/70 dark:hover:bg-green-900/20'
                                  : 'hover:bg-slate-50 dark:hover:bg-slate-700/40'
                          }`}
                        >
                          <span className={`text-center text-[11px] leading-tight w-full break-words ${
                            member ? (statusClass || 'text-slate-700 dark:text-slate-200') : 'text-slate-300 dark:text-slate-600'
                          }`}>
                            {member ? member.name : '—'}
                          </span>
                        </button>

                        {/* Remove team — visible on row hover, first cell of group, write-only */}
                        {i === 0 && !readOnly && (
                          <DeleteButton
                            onConfirm={() => onRemoveTeam?.(service.service_id, col.groupName)}
                            className="absolute top-0 right-0 z-10 opacity-0 group-hover/row:opacity-100 border-l border-b border-slate-200 dark:border-slate-700 px-1 py-0.5 rounded-bl transition-opacity"
                            iconSize="w-2.5 h-2.5"
                            title={`Remove ${col.groupName} from this service`}
                            testId={`remove-team-btn-${service.service_id}-${col.groupName}`}
                          />
                        )}

                        {!readOnly && isActive && (
                          <MemberDropdown
                            buttonRect={activeCell.rect}
                            role={role}
                            currentMemberId={memberId}
                            roleMemberIds={roleMemberSet}
                            allMembersList={allMembers}
                            blockedMemberIds={getBlockedForDate(service.date)}
                            onSelect={(mid) => handleAssign(service.service_id, col.groupName, role.name, mid)}
                          />
                        )}
                      </td>
                    );
                  });
                })}
              </tr>
            );
          })}

          {services.length === 0 && (
            <tr>
              <td colSpan={flatRoles.length + 1} className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">
                No upcoming services
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SchedulerGrid;
